/*
* Palo Alto Theme
*
* Use this file to add custom Javascript to Palo Alto.  Keeping your custom
* Javascript in this fill will make it easier to update Palo Alto. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

(function () {
  if (window.__nohoFirstOrderCartInit) return;
  window.__nohoFirstOrderCartInit = true;

  function cartJsUrl(route) {
    if (!route) return '/cart/update.js';
    const base = String(route).split('?')[0];
    return base.endsWith('.js') ? base : `${base}.js`;
  }

  function getFirstOrderEls() {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return {};
    const wrapper = drawer.querySelector('[data-first-order-wrapper]');
    const checkbox = drawer.querySelector('[data-first-order-checkbox]');
    return { drawer, wrapper, checkbox };
  }

  function getFirstOrderNoteText() {
    const { wrapper } = getFirstOrderEls();
    const el = wrapper?.querySelector('[data-first-order-note-json]');
    if (!el?.textContent) return '';
    try {
      const parsed = JSON.parse(el.textContent.trim());
      return typeof parsed === 'string' ? parsed.trim() : '';
    } catch {
      return '';
    }
  }

  function getFirstOrderVariantId() {
    const { wrapper } = getFirstOrderEls();
    const raw = wrapper?.getAttribute('data-first-order-variant-id');
    if (!raw) {
      const fallbackRaw = window.theme?.settings?.add_product_to_cart;
      const fallback = parseInt(fallbackRaw, 10);
      return Number.isFinite(fallback) ? fallback : null;
    }
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
  }

  function normalizeHandle(value) {
    return String(value || '')
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function getFirstOrderProductHandle() {
    const raw = window.theme?.settings?.add_product_to_cart;
    if (!raw) return '';
    if (Number.isFinite(parseInt(raw, 10))) return '';
    return normalizeHandle(raw);
  }

  function getNoteField() {
    const { drawer } = getFirstOrderEls();
    if (!drawer) return null;
    return drawer.querySelector('form#cartDrawerCheckoutForm textarea[name="note"]');
  }

  function refreshThemeCart() {
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  function fetchCartJson() {
    const root = window.theme?.routes?.root || '/';
    return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
  }

  function cartContainsVariant(cart, variantId) {
    return (cart.items || []).some((item) => String(item.variant_id) === String(variantId));
  }

  function cartOnlyContainsVariant(cart, variantId) {
    const items = cart.items || [];
    if (items.length !== 1) return false;
    return String(items[0].variant_id) === String(variantId);
  }

  function isFirstOrderKitItem(item, variantId, productHandle) {
    if (!item) return false;
    if (variantId && String(item.variant_id) === String(variantId)) return true;
    if (!productHandle) {
      const handleGuess = normalizeHandle(item.handle || item.product_handle || item.product_title || item.title || '');
      const titleGuess = String(item.product_title || item.title || '').toLowerCase();
      const isSizingKitByName = handleGuess.includes('sizing-kit') || (titleGuess.includes('sizing') && titleGuess.includes('kit'));
      return isSizingKitByName && Number(item.final_price || 0) === 0;
    }
    const itemHandle = normalizeHandle(item.handle || item.product_handle || '');
    return itemHandle === productHandle;
  }

  function cartOnlyContainsFirstOrderKit(cart, variantId, productHandle) {
    const items = cart.items || [];
    if (items.length !== 1) return false;
    return isFirstOrderKitItem(items[0], variantId, productHandle);
  }

  function isLikelyFreeSizingKitOnlyItem(cart) {
    const items = cart.items || [];
    if (items.length !== 1) return false;
    const item = items[0] || {};
    const title = String(item.product_title || item.title || '').toLowerCase();
    const handle = normalizeHandle(item.handle || item.product_handle || '');
    const isFree = Number(item.final_price || 0) === 0;
    return isFree && ((title.includes('sizing') && title.includes('kit')) || handle.includes('sizing-kit'));
  }

  function cartContainsFirstOrderKit(cart, variantId, productHandle) {
    return (cart.items || []).some((item) => isFirstOrderKitItem(item, variantId, productHandle));
  }

  function cartHasPressOnNailsItem() {
    return Boolean(document.querySelector('[data-cart-item][data-has-press-on-nails="true"]'));
  }

  function syncFirstOrderCheckboxFromCart() {
    const { wrapper, checkbox } = getFirstOrderEls();
    if (!wrapper || !checkbox) return;

    const shouldShow = cartHasPressOnNailsItem();
    wrapper.classList.toggle('hidden', !shouldShow);
    checkbox.checked = shouldShow ? checkbox.checked : false;
    if (!shouldShow) return;
    if (checkbox.disabled) return;

    const variantId = getFirstOrderVariantId(); 
    if (!variantId) return;

    fetchCartJson()
      .then((cart) => {
        checkbox.checked = cartContainsVariant(cart, variantId);
      })
      .catch(() => {});
  }

  let firstOrderEligibilityEnforcing = false;
  function enforceFirstOrderKitEligibility() {
    if (firstOrderEligibilityEnforcing) return;

    const variantId = getFirstOrderVariantId();
    const productHandle = getFirstOrderProductHandle();

    firstOrderEligibilityEnforcing = true;
    fetchCartJson()
      .then((cart) => {
        if (isLikelyFreeSizingKitOnlyItem(cart)) {
          return removeSingleItemCartAndMaybeNote(cart).then(() => {
            refreshThemeCart();
            if (window.location.pathname.includes('/cart')) {
              window.location.reload();
            }
          });
        }

        // If the free sizing kit is the only line item, remove it.
        if (cartOnlyContainsFirstOrderKit(cart, variantId, productHandle)) {
          return removeMatchingFirstOrderItemsAndMaybeNote(cart, variantId, productHandle).then(() => {
            refreshThemeCart();
            if (window.location.pathname.includes('/cart')) {
              window.location.reload();
            }
          });
        }

        if (cartHasPressOnNailsItem()) return;
        if (!cartContainsFirstOrderKit(cart, variantId, productHandle)) return;
        return removeMatchingFirstOrderItemsAndMaybeNote(cart, variantId, productHandle).then(() => {
          refreshThemeCart();
        });
      })
      .catch(() => {})
      .finally(() => {
        firstOrderEligibilityEnforcing = false;
      });
  }

  function setFirstOrderLoading(on) {
    const { wrapper } = getFirstOrderEls();
    if (!wrapper) return;
    wrapper.classList.toggle('is-first-order-loading', Boolean(on));
    const cb = wrapper.querySelector('[data-first-order-checkbox]');
    const variantId = getFirstOrderVariantId();
    if (cb) cb.disabled = Boolean(on) || !variantId;
  }

  function addFirstOrderItemAndNote(variantId) {
    const addUrl = cartJsUrl(window.theme?.routes?.cart_add_url);
    const updateUrl = cartJsUrl(window.theme?.routes?.cart_update_url);

    return fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data && data.status) {
          throw new Error(data.description || data.message || 'Could not add sizing kit');
        }
        const note = getFirstOrderNoteText();
        if (!note) return null;
        return fetch(updateUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ note }),
        });
      })
      .then((r) => {
        if (r === null) return {};
        if (!r.ok) throw new Error('Could not update cart note');
        return r.json();
      });
  }

  function removeFirstOrderItemAndMaybeNote(variantId) {
    const updateUrl = cartJsUrl(window.theme?.routes?.cart_update_url);
    const noteField = getNoteField();
    const currentNote = (noteField?.value || '').trim();
    const expectedNote = getFirstOrderNoteText();
    const payload = {
      updates: { [String(variantId)]: 0 },
    };
    if (expectedNote && currentNote === expectedNote) {
      payload.note = '';
    }

    return fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw new Error('Could not update cart');
      return r.json();
    });
  }

  function removeMatchingFirstOrderItemsAndMaybeNote(cart, variantId, productHandle) {
    const updateUrl = cartJsUrl(window.theme?.routes?.cart_update_url);
    const noteField = getNoteField();
    const currentNote = (noteField?.value || '').trim();
    const expectedNote = getFirstOrderNoteText();
    const updates = {};

    (cart.items || []).forEach((item) => {
      if (!isFirstOrderKitItem(item, variantId, productHandle)) return;
      if (item.key) {
        updates[item.key] = 0;
      } else if (variantId) {
        updates[String(variantId)] = 0;
      }
    });

    if (!Object.keys(updates).length) return Promise.resolve({});

    const payload = { updates };
    if (expectedNote && currentNote === expectedNote) {
      payload.note = '';
    }

    return fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw new Error('Could not update cart');
      return r.json();
    });
  }

  function removeSingleItemCartAndMaybeNote(cart) {
    const updateUrl = cartJsUrl(window.theme?.routes?.cart_update_url);
    const noteField = getNoteField();
    const currentNote = (noteField?.value || '').trim();
    const expectedNote = getFirstOrderNoteText();
    const item = (cart.items || [])[0];
    if (!item) return Promise.resolve({});

    const payload = {
      updates: { [String(item.key || item.variant_id)]: 0 },
    };

    if (expectedNote && currentNote === expectedNote) {
      payload.note = '';
    }

    return fetch(updateUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    }).then((r) => {
      if (!r.ok) throw new Error('Could not update cart');
      return r.json();
    });
  }

  function onFirstOrderChange(event) {
    const target = event.target;
    if (!target.matches?.('[data-first-order-checkbox]')) return;
    if (!document.getElementById('cart-drawer')?.contains(target)) return;

    const variantId = getFirstOrderVariantId();
    if (!variantId) return;

    const checked = target.checked;
    setFirstOrderLoading(true);

    const done = () => setFirstOrderLoading(false);

    const op = checked
      ? addFirstOrderItemAndNote(variantId)
      : removeFirstOrderItemAndMaybeNote(variantId);

    op.then(() => {
      refreshThemeCart();
    })
      .catch(() => {
        target.checked = !checked;
      })
      .finally(done);
  }

  document.addEventListener('change', onFirstOrderChange);
  document.addEventListener('theme:cart:added', syncFirstOrderCheckboxFromCart);
  document.addEventListener('theme:cart:loaded', syncFirstOrderCheckboxFromCart);
  document.addEventListener('theme:cart:update', syncFirstOrderCheckboxFromCart);
  document.addEventListener('theme:cart:added', enforceFirstOrderKitEligibility);
  document.addEventListener('theme:cart:loaded', enforceFirstOrderKitEligibility);
  document.addEventListener('theme:cart:update', enforceFirstOrderKitEligibility);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enforceFirstOrderKitEligibility, { once: true });
  } else {
    enforceFirstOrderKitEligibility();
  }
})();

// Swym callback: keep theme cart state in sync when adding from wishlist drawer
function onSwymLoadCallback(swat) {
  const event = 'sw:addedtocart';
  if (window.__swymCartSyncBound) return;
  window.__swymCartSyncBound = true;

  const listener = function () {
    waitForCartUpdate().then(() => {
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('theme:cart:open', { bubbles: true }));
      }, 120);
    });
  };
  swat.evtLayer.addEventListener(event, listener);
}

if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}

window.SwymCallbacks.push(onSwymLoadCallback);
function waitForCartUpdate() {
  return new Promise((resolve) => {
    let attempts = 0;

    const checkCart = () => {
      fetch('/cart.js')
        .then((res) => res.json())
        .then((cart) => {
          if (cart.item_count > 0 || attempts > 10) {
            resolve(cart);
          } else {
            attempts++;
            setTimeout(checkCart, 300);
          }
        })
        .catch(() => {
          attempts++;
          if (attempts > 10) resolve();
          else setTimeout(checkCart, 300);
        });
    };

    setTimeout(checkCart, 300);
  });
}

